import { fetcher } from '@chipinside/fetcher'
import { Translate } from '@chipinside/translate'
import { Box } from '@mui/material'
import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { useLoaderContext } from '#/contexts/Loader'
import PageError from '#/pages/Fallback/PageError'
import { login, useUserInfo } from '#/store/ducks/auth'
import { resetFarm } from '#/store/ducks/farm'
import { endpoint } from '#/store/services/endpoints'
import { Sentry } from '#/store/services/Sentry'

export type UserInfoType = {
  active_farm_count: number
  birth: string | null
  email: string
  farm_count: number
  has_company: boolean
  is_system_user: boolean
  isSuspended?: boolean
  locale_text: string
  locale: string
  name: string
  phone: string
  prefer_pagination: boolean
  slug: string
  uuid: string
}

const IGNORED_LIST_PAGES = ['/maintenance', '/suspended-account', '/logout', '/bem-estar/frankanna']

const PLAYWRIGHT_EMAIL = 'playwright@chipinside.com.br'

export function withUser(Component: React.FC) {
  const WithUser = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const { setOptions } = useLoaderContext()
    const location = useLocation()
    const isFarmContext = location.pathname.startsWith('/farm/')
    const userInfo = useUserInfo() as UserInfoType
    const [statusCode, setStatusCode] = useState<number | null>(null)
    const fetchData = () => {
      /*
       * Exibe loader de carregamento de informações do usuário
       */
      setOptions({
        loading: true,
        title: Translate({ messageKey: 'loading_user_info' }),
      })

      dispatch<any>(login())
        .unwrap()
        .then(({ user }: { user: UserInfoType }) => {
          Sentry.setUser(user)

          Sentry.hitBrowser()

          /**
           * Se o usuário estiver suspenso, redireciona para a página de conta suspensa
           */
          if (user?.isSuspended) {
            history.push('/suspended-account')
          }

          /**
           * Se não estiver no contexto da fazenda e o usuário tiver apenas uma fazenda
           * Redireciona para a página da fazenda
           * Exceto para o usuário do Playwright, que não deve ser redirecionado automaticamente
           */
          if (!isFarmContext && user?.farm_count === 1 && user?.email !== PLAYWRIGHT_EMAIL) {
            /*
             * Exibe loader de redirecionamento para a fazenda
             */
            setOptions({
              loading: true,
              title: Translate({ messageKey: 'redirecting_to_farm' }),
            })

            /**
             * Faz a requisição para buscar a lista de fazendas
             */
            fetcher<any>({
              controller: endpoint.farm.access_datatable(),
            })
              // @ts-ignore
              .then(({ data }) => {
                const farm_slug = data?.data?.[0]?.slug
                /**
                 * Se a fazenda existir, redireciona para a página da fazenda
                 */
                if (farm_slug) {
                  history.push(`/farm/${farm_slug}`)
                }
              })
              .catch(() => {
                history.push(`/`)
              })
          }
        })
        .catch((e: any) => {
          console.warn(`[${e?.status}] withUser Error: ${e?.data?.error?.message}`)
          setStatusCode(e?.status ?? 'error')
          setOptions({ loading: false })
        })
    }

    useEffect(() => {
      if (IGNORED_LIST_PAGES.includes(location.pathname)) {
        setOptions({ loading: false })
      } else {
        if (isEmpty(userInfo)) {
          fetchData()
        }

        // Se o usuário é o de teste não pode ser redirecionado para a fazenda
        const skipsFarmRedirect = userInfo?.farm_count !== 1 || userInfo?.email === PLAYWRIGHT_EMAIL

        if (!isEmpty(userInfo) && skipsFarmRedirect && !isFarmContext) {
          setOptions({ loading: false })
        }
      }

      return () => {
        /**
         * Se o usuário não estiver no contexto da fazenda, reseta a fazenda atual
         * e as permissões da fazenda
         */
        if (!isFarmContext) {
          dispatch(resetFarm())
        }
      }
    }, [isFarmContext, userInfo])

    if (statusCode) {
      return (
        <Box sx={{ height: '100vh' }}>
          <PageError code={statusCode} />
        </Box>
      )
    }

    return !isEmpty(userInfo) || IGNORED_LIST_PAGES.includes(location.pathname) ? (
      <Component />
    ) : null
  }

  return WithUser
}
